// services/v3/datasource/RtdbSource.ts — today's backend, READ ONLY (SPEC §4.2)
//
// RTDB holds the catalog (`stores/{storeId}/settings|products`) and the live
// table nodes; Firestore holds soldout, the qrcode tokens and the single write
// V3 is allowed to perform (`qrcode_orders/{jobId}` inside submitOrder).
//
// Per SPEC §0 this is one of only two V3 files allowed to import
// `firebase/database`, and only `ref, child, get, onValue` (listeners are removed
// through the unsubscribe onValue returns, never a path-wide `off`).
import { child, get, onValue, ref as dbRef } from 'firebase/database'
import {
  doc as fsDoc,
  getDoc,
  onSnapshot,
  runTransaction,
} from 'firebase/firestore'
import type {
  DataSource, DataSourceContext, EntityVersions, Unsubscribe, Versioned,
} from '~/types/v3/datasource'
import type {
  ChannelPriceMap, ProductAllowMap, ProductsPayload, SettingsPayload,
  SoldoutMap, StoreProfile, TableDoc, TableItem, TableState,
} from '~/types/v3/entities'
import type { QrToken } from '~/types/v3/token'
import type { QrJobCard, SubmitResult } from '~/types/v3/job'
import { hashJson } from '~/utils/v3/hash'
import { readV3Config } from '../config'
import { getFs, getRtdb, getServerOffset } from '../firebase'
import { assertWriteAllowed } from '../writeGuard'
import {
  normalizeProducts, normalizeSettings, pickChannelPrice, pickMenuSet,
} from './normalize'

/** Thrown inside the submit transaction when the qrcode doc no longer exists. */
export class SessionEndedError extends Error {
  constructor(message = 'session ended') {
    super(message)
    this.name = 'SessionEndedError'
  }
}

const NETWORK_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'cancelled',
  'resource-exhausted',
  'internal',
])

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

/** Maps a thrown value onto the SubmitResult failure reasons (SPEC §4.1). */
function submitFailureReason(err: unknown): 'session_ended' | 'network' | 'unknown' {
  if (err instanceof SessionEndedError) return 'session_ended'
  const code = isRecord(err) && typeof err.code === 'string' ? err.code : ''
  const bare = code.includes('/') ? code.slice(code.indexOf('/') + 1) : code
  if (NETWORK_CODES.has(bare)) return 'network'
  const message = err instanceof Error ? err.message.toLowerCase() : ''
  if (message.includes('network') || message.includes('offline') || message.includes('failed to fetch')) {
    return 'network'
  }
  return 'unknown'
}

/** RTDB arrays come back sparse-as-maps; flatten either shape, dropping nulls. */
function toArray<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw.filter((x) => x !== null && x !== undefined) as T[]
  if (isRecord(raw)) return Object.values(raw).filter((x) => x !== null && x !== undefined) as T[]
  return []
}

export class RtdbSource implements DataSource {
  readonly kind = 'rtdb' as const

  private ctx: DataSourceContext | null = null
  private hasVersionsNode = false
  private unsubs: Unsubscribe[] = []

  /** memo of the most recent settings fetch — reused by channel_price / menuSet */
  private settingsMemo: Versioned<SettingsPayload> | null = null
  private settingsInFlight: Promise<Versioned<SettingsPayload>> | null = null

  /** may only be known after init() */
  get supportsVersions(): boolean {
    return this.hasVersionsNode
  }

  // -------------------------------------------------------------------------
  // lifecycle
  // -------------------------------------------------------------------------

  async init(ctx: DataSourceContext): Promise<void> {
    this.ctx = ctx
    this.settingsMemo = null
    this.settingsInFlight = null
    this.hasVersionsNode = false
    try {
      const snap = await get(dbRef(getRtdb(), `${this.base()}/versions`))
      this.hasVersionsNode = snap.exists() && isRecord(snap.val())
    } catch (e) {
      console.warn('[v3] rtdb versions probe failed', e)
      this.hasVersionsNode = false
    }
  }

  dispose(): void {
    const held = this.unsubs
    this.unsubs = []
    for (const u of held) {
      try { u() } catch (e) { console.warn('[v3] rtdb unsubscribe failed', e) }
    }
    this.settingsMemo = null
    this.settingsInFlight = null
  }

  private base(): string {
    return `stores/${this.requireCtx().storeId}`
  }

  private requireCtx(): DataSourceContext {
    if (!this.ctx) throw new Error('[v3] RtdbSource used before init()')
    return this.ctx
  }

  private track(unsub: Unsubscribe): Unsubscribe {
    let done = false
    const wrapped: Unsubscribe = () => {
      if (done) return
      done = true
      const at = this.unsubs.indexOf(wrapped)
      if (at >= 0) this.unsubs.splice(at, 1)
      try { unsub() } catch (e) { console.warn('[v3] rtdb unsubscribe failed', e) }
    }
    this.unsubs.push(wrapped)
    return wrapped
  }

  // -------------------------------------------------------------------------
  // versions (optional interim node `stores/{storeId}/versions`)
  // -------------------------------------------------------------------------

  private mapVersions(raw: unknown): EntityVersions | null {
    if (!isRecord(raw)) return null
    const settings = String(raw.settings ?? '')
    const products = String(raw.products ?? '')
    return { settings, products, channel_price: settings, menuSet: settings }
  }

  async getVersions(): Promise<EntityVersions | null> {
    if (!this.hasVersionsNode) return null
    try {
      const snap = await get(dbRef(getRtdb(), `${this.base()}/versions`))
      return this.mapVersions(snap.val())
    } catch (e) {
      console.warn('[v3] getVersions failed', e)
      return null
    }
  }

  watchVersions(cb: (v: EntityVersions) => void): Unsubscribe {
    if (!this.hasVersionsNode) return () => { /* unsupported */ }
    const node = dbRef(getRtdb(), `${this.base()}/versions`)
    const unsub = onValue(node, (snap) => {
      const v = this.mapVersions(snap.val())
      if (v) cb(v)
    }, (e) => console.warn('[v3] watchVersions error', e))
    return this.track(unsub)
  }

  // -------------------------------------------------------------------------
  // catalog fetches
  // -------------------------------------------------------------------------

  fetchSettings(): Promise<Versioned<SettingsPayload>> {
    if (this.settingsInFlight) return this.settingsInFlight
    const p = (async () => {
      const snap = await get(dbRef(getRtdb(), `${this.base()}/settings`))
      const data = normalizeSettings(snap.val())
      const versioned: Versioned<SettingsPayload> = { version: hashJson(data.list), data }
      this.settingsMemo = versioned
      return versioned
    })().finally(() => { this.settingsInFlight = null })
    this.settingsInFlight = p
    return p
  }

  /** The memoised settings, fetching once if nothing has been read yet. */
  private async settings(): Promise<Versioned<SettingsPayload>> {
    return this.settingsMemo ?? this.fetchSettings()
  }

  async fetchProducts(): Promise<Versioned<ProductsPayload>> {
    const snap = await get(dbRef(getRtdb(), `${this.base()}/products`))
    const data = normalizeProducts(snap.val())
    return { version: hashJson(data), data }
  }

  async fetchChannelPrice(): Promise<Versioned<ChannelPriceMap>> {
    const s = await this.settings()
    return { version: s.version, data: pickChannelPrice(s.data) }
  }

  async fetchMenuSet(settingId: string): Promise<Versioned<ProductAllowMap>> {
    const s = await this.settings()
    return { version: s.version, data: pickMenuSet(s.data, settingId) }
  }

  // -------------------------------------------------------------------------
  // store profile (HTTP)
  // -------------------------------------------------------------------------

  async getStoreProfile(): Promise<StoreProfile | null> {
    try {
      const { storeId } = this.requireCtx()
      const base = readV3Config().storeApiBase
      if (!base || !storeId) return null
      const res = await fetch(`${base.replace(/\/+$/, '')}/stores/${storeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storeId}`,
        },
      })
      if (!res.ok) return null
      const body: unknown = await res.json()
      const name = isRecord(body) && typeof body.name === 'string' ? body.name : ''
      return { name }
    } catch (e) {
      console.warn('[v3] getStoreProfile failed', e)
      return null
    }
  }

  // -------------------------------------------------------------------------
  // realtime watches
  // -------------------------------------------------------------------------

  watchSoldout(cb: (m: SoldoutMap) => void): Unsubscribe {
    const ref = fsDoc(getFs(), `${this.base()}/appState/soldout`)
    const unsub = onSnapshot(
      ref,
      (snap) => cb(snap.exists() ? (snap.data() as SoldoutMap) : {}),
      (e) => console.warn('[v3] watchSoldout error', e),
    )
    return this.track(unsub)
  }

  watchTableState(table: string, cb: (s: TableState | null) => void): Unsubscribe {
    const node = child(dbRef(getRtdb(), `${this.base()}/tableState`), table)
    const unsub = onValue(node, (snap) => {
      const v = snap.val()
      cb(isRecord(v) ? (v as TableState) : null)
    }, (e) => console.warn('[v3] watchTableState error', e))
    return this.track(unsub)
  }

  watchTableItems(table: string, cb: (d: TableDoc | null) => void): Unsubscribe {
    const node = child(dbRef(getRtdb(), `${this.base()}/tables`), table)
    const unsub = onValue(node, (snap) => {
      const v = snap.val()
      if (!isRecord(v)) { cb(null); return }
      cb({ items: toArray<TableItem>(v.items) })
    }, (e) => console.warn('[v3] watchTableItems error', e))
    return this.track(unsub)
  }

  watchQrcode(qrcodeId: string, cb: (t: QrToken | null) => void): Unsubscribe {
    const ref = fsDoc(getFs(), 'qrcodes', qrcodeId)
    const unsub = onSnapshot(
      ref,
      (snap) => cb(snap.exists() ? (snap.data() as QrToken) : null),
      (e) => console.warn('[v3] watchQrcode error', e),
    )
    return this.track(unsub)
  }

  async getQrcode(qrcodeId: string): Promise<QrToken | null> {
    const snap = await getDoc(fsDoc(getFs(), 'qrcodes', qrcodeId))
    return snap.exists() ? (snap.data() as QrToken) : null
  }

  async getTableState(table: string): Promise<TableState | null> {
    const snap = await get(child(dbRef(getRtdb(), `${this.base()}/tableState`), table))
    const v = snap.val()
    return isRecord(v) ? (v as TableState) : null
  }

  async getServerNowISO(): Promise<string> {
    const offset = await getServerOffset()
    return new Date(Date.now() + offset).toISOString()
  }

  // -------------------------------------------------------------------------
  // the one write (SPEC §4.1)
  // -------------------------------------------------------------------------

  async submitOrder(job: QrJobCard): Promise<SubmitResult> {
    const ctx = this.requireCtx()
    try {
      assertWriteAllowed('qrcode_orders')
    } catch (error) {
      return { ok: false, jobId: job.jobId, reason: 'unknown', error }
    }

    // lock preflight — the cashier is settling the bill
    try {
      const state = await this.getTableState(job.table || ctx.table)
      if (state?.lock === true) return { ok: false, jobId: job.jobId, reason: 'locked' }
    } catch (e) {
      console.warn('[v3] lock preflight failed', e)
    }

    const fs = getFs()
    const qrcodeRef = fsDoc(fs, 'qrcodes', job.qrcodeId || ctx.qrcodeId)
    const orderRef = fsDoc(fs, `${this.base()}/qrcode_orders/${job.jobId}`)

    try {
      const result = await runTransaction(fs, async (tx) => {
        const q = await tx.get(qrcodeRef)
        if (!q.exists()) throw new SessionEndedError()
        const o = await tx.get(orderRef)
        if (o.exists()) return 'exists' as const
        tx.set(orderRef, job)
        return 'created' as const
      })
      return { ok: true, jobId: job.jobId, simulated: false, alreadyExisted: result === 'exists' }
    } catch (error) {
      return { ok: false, jobId: job.jobId, reason: submitFailureReason(error), error }
    }
  }
}
